import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, ImagePlus, Trash2, MapPin, Search } from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getImageUrl } from "../../utils/getImageUrl";
import CountryPicker from "../CountryPicker";

// All IANA timezones supported by the browser
const TIMEZONES =
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : ["America/Bogota", "America/Caracas", "America/New_York", "Europe/Madrid", "UTC"];

const DEFAULT_SCHEDULE = [
  { day: "monday",    open: "08:00", close: "18:00", closed: false },
  { day: "tuesday",   open: "08:00", close: "18:00", closed: false },
  { day: "wednesday", open: "08:00", close: "18:00", closed: false },
  { day: "thursday",  open: "08:00", close: "18:00", closed: false },
  { day: "friday",    open: "08:00", close: "18:00", closed: false },
  { day: "saturday",  open: "09:00", close: "14:00", closed: false },
  { day: "sunday",    open: "09:00", close: "14:00", closed: true  },
];

const DAY_LABELS_ES = {
  monday: "Lunes", tuesday: "Martes", wednesday: "Miércoles",
  thursday: "Jueves", friday: "Viernes", saturday: "Sábado", sunday: "Domingo",
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function LocationPicker({ location, setLocation }) {
  useMapEvents({ click(e) { setLocation({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return location.lat && location.lng
    ? <Marker position={[parseFloat(location.lat), parseFloat(location.lng)]} />
    : null;
}

// Flies the map to a new position whenever flyTo changes
function MapController({ flyTo }) {
  const map = useMap();
  useEffect(() => {
    if (flyTo) map.flyTo(flyTo, 16, { duration: 1 });
  }, [flyTo, map]);
  return null;
}

export default function CreateSedeModal({ companyId, onClose, onSedeCreated, sedeToEdit }) {
  const [loading,    setLoading]    = useState(false);
  const [form,       setForm]       = useState({
    name: "", address: "", email: "", phone: "", phoneCountryCode: "+58",
    location: { lat: "", lng: "" },
    social: { facebook: "", instagram: "", web: "" },
    timezone: "America/Caracas",
    schedule: DEFAULT_SCHEDULE,
  });
  const [existingImages, setExistingImages] = useState([]);
  const [newImages,      setNewImages]      = useState([]);
  const [coverIndex,     setCoverIndex]     = useState(0);

  // Geocoding state
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [flyTo,         setFlyTo]         = useState(null);
  const [gpsLoading,    setGpsLoading]    = useState(false);
  const searchTimer  = useRef(null);

  useEffect(() => {
    if (!sedeToEdit) return;
    const lat = sedeToEdit.lat ?? sedeToEdit.location?.lat ?? sedeToEdit.location?.coordinates?.[1] ?? "";
    const lng = sedeToEdit.lng ?? sedeToEdit.location?.lng ?? sedeToEdit.location?.coordinates?.[0] ?? "";

    setForm({
      name:     sedeToEdit.name     || "",
      address:  sedeToEdit.address  || "",
      email:    sedeToEdit.email    || "",
      phone:    sedeToEdit.phone    || "",
      phoneCountryCode: sedeToEdit.phoneCountryCode || "+58",
      location: { lat, lng },
      social: {
        facebook:  sedeToEdit.facebook  ?? sedeToEdit.social?.facebook  ?? "",
        instagram: sedeToEdit.instagram ?? sedeToEdit.social?.instagram ?? "",
        web:       sedeToEdit.web       ?? sedeToEdit.social?.web       ?? "",
      },
      timezone: sedeToEdit.timezone || "America/Caracas",
      schedule:
        Array.isArray(sedeToEdit.schedules) && sedeToEdit.schedules.length > 0 ? sedeToEdit.schedules
        : Array.isArray(sedeToEdit.schedule) && sedeToEdit.schedule.length > 0 ? sedeToEdit.schedule
        : DEFAULT_SCHEDULE,
    });
    setExistingImages(sedeToEdit.images || []);
    const cover = sedeToEdit.images?.findIndex((i) => i.isCover);
    if (cover >= 0) setCoverIndex(cover);
    if (lat && lng) setFlyTo([parseFloat(lat), parseFloat(lng)]);
  }, [sedeToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["facebook", "instagram", "web"].includes(name)) {
      setForm((p) => ({ ...p, social: { ...p.social, [name]: value } }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  // Accumulate selected files instead of replacing
  const handleNewImages = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => {
      const combined = [...prev, ...files];
      if (combined.length + existingImages.length > 5) {
        toast.warning("Máximo 5 imágenes");
        return prev;
      }
      return combined;
    });
    e.target.value = ""; // allow re-selecting same file
  };

  const removeExistingImage = (index) => setExistingImages((p) => p.filter((_, i) => i !== index));
  const removeNewImage      = (index) => setNewImages((p) => p.filter((_, i) => i !== index));

  // Nominatim geocoding with 600ms debounce
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResults([]);
    clearTimeout(searchTimer.current);
    if (q.length < 3) return;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`,
          { headers: { "Accept-Language": navigator.language || "es" } }
        );
        setSearchResults(await res.json());
      } catch { /* silently ignore */ }
      finally { setSearching(false); }
    }, 600);
  };

  const selectResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const address = result.display_name.split(",").slice(0, 3).join(",").trim();
    setForm((p) => ({ ...p, location: { lat, lng }, address }));
    setFlyTo([lat, lng]);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleGPS = () => {
    if (!navigator.geolocation) { toast.error("Tu navegador no soporta geolocalización"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const lat = coords.latitude;
        const lng = coords.longitude;
        setForm((p) => ({ ...p, location: { lat, lng } }));
        setFlyTo([lat, lng]);
        setGpsLoading(false);
      },
      () => { toast.error("No se pudo obtener tu ubicación"); setGpsLoading(false); }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.email || !form.phone || !form.location.lat) {
      toast.warning("Completa todos los campos obligatorios y selecciona la ubicación en el mapa");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("name",     form.name);
      fd.append("address",  form.address);
      fd.append("email",    form.email);
      fd.append("phone",    form.phone);
      fd.append("phoneCountryCode", form.phoneCountryCode);
      fd.append("lat",      form.location.lat);
      fd.append("lng",      form.location.lng);
      fd.append("location[lat]", form.location.lat);
      fd.append("location[lng]", form.location.lng);
      fd.append("timezone", form.timezone);
      fd.append("schedule", JSON.stringify(form.schedule));
      Object.entries(form.social).forEach(([k, v]) => {
        fd.append(k, v);
        fd.append(`social[${k}]`, v);
      });
      fd.append("coverIndex", coverIndex);
      existingImages.forEach((img, i) => {
        fd.append(`existingImages[${i}][url]`,     img.url);
        fd.append(`existingImages[${i}][isCover]`, i === coverIndex);
      });
      newImages.forEach((img) => fd.append("images", img));

      if (sedeToEdit) {
        await API.put(`/sedes/${sedeToEdit.id ?? sedeToEdit._id}`, fd);
        toast.success("Sede actualizada");
      } else {
        await API.post(`/sedes/company/${companyId}`, fd);
        toast.success("Sede creada");
      }
      onSedeCreated();
      onClose();
    } catch (err) {
      toast.error("Error guardando sede");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const mapCenter = form.location.lat
    ? [parseFloat(form.location.lat), parseFloat(form.location.lng)]
    : [4.6097, -74.0817];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white p-4 sm:p-6 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-semibold text-lg">
            {sedeToEdit ? "Editar sede" : "Crear nueva sede"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">

          {/* ── Información básica ──────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input name="name"    value={form.name}    onChange={handleChange} placeholder="Nombre *"    className="p-3 border rounded-xl" />
            <input name="address" value={form.address} onChange={handleChange} placeholder="Dirección *" className="p-3 border rounded-xl" />
            <input name="email"   value={form.email}   onChange={handleChange} placeholder="Correo *"    className="p-3 border rounded-xl" />
            <div className="flex gap-2">
              <CountryPicker
                value={form.phoneCountryCode}
                onChange={(c) => setForm((p) => ({ ...p, phoneCountryCode: c }))}
              />
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Teléfono *"
                className="flex-1 min-w-0 p-3 border rounded-xl"
              />
            </div>
          </div>

          {/* ── Mapa + Geocoding ────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Ubicación *</h3>

            {/* Search bar + GPS button */}
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Buscar dirección en el mapa..."
                  className="w-full pl-8 pr-3 py-2 border rounded-xl text-sm"
                />
                {searching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    Buscando...
                  </span>
                )}
                {searchResults.length > 0 && (
                  <ul className="absolute z-[1000] w-full bg-white border rounded-xl mt-1 shadow-lg max-h-48 overflow-y-auto text-sm">
                    {searchResults.map((r) => (
                      <li
                        key={r.place_id}
                        onClick={() => selectResult(r)}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {r.display_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={handleGPS}
                disabled={gpsLoading}
                className="flex items-center gap-1.5 px-3 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                <MapPin size={15} />
                {gpsLoading ? "..." : "Mi ubicación"}
              </button>
            </div>

            {/* Map */}
            <MapContainer
              center={mapCenter}
              zoom={form.location.lat ? 15 : 5}
              className="h-64 sm:h-[380px] rounded-xl"
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapController flyTo={flyTo} />
              <LocationPicker
                location={form.location}
                setLocation={(loc) => setForm((p) => ({ ...p, location: loc }))}
              />
            </MapContainer>

            {/* Coordinates */}
            {form.location.lat ? (
              <div className="flex gap-3 mt-2">
                <div className="flex-1">
                  <label className="text-xs text-gray-400 block mb-0.5">Latitud</label>
                  <input
                    type="number"
                    step="any"
                    value={form.location.lat}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, location: { ...p.location, lat: e.target.value } }))
                    }
                    className="w-full p-2 border rounded-lg text-sm font-mono"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-400 block mb-0.5">Longitud</label>
                  <input
                    type="number"
                    step="any"
                    value={form.location.lng}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, location: { ...p.location, lng: e.target.value } }))
                    }
                    className="w-full p-2 border rounded-lg text-sm font-mono"
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-1.5 text-center">
                Haz click en el mapa, busca una dirección o usa tu ubicación GPS
              </p>
            )}
          </div>

          {/* ── Imágenes ─────────────────────────────────────────── */}
          <div>
            <label className="flex gap-2 items-center cursor-pointer text-sm text-gray-600 mb-2 w-fit">
              <ImagePlus size={16} />
              Agregar imágenes (máx. 5)
              <input type="file" multiple hidden accept="image/*" onChange={handleNewImages} />
            </label>
            {(existingImages.length > 0 || newImages.length > 0) && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {existingImages.map((img, i) => (
                  <div
                    key={i}
                    onClick={() => setCoverIndex(i)}
                    className={`relative border rounded-lg cursor-pointer overflow-hidden ${
                      coverIndex === i ? "ring-2 ring-supernova-500" : ""
                    }`}
                  >
                    <img
                      src={getImageUrl(img.url)}
                      alt=""
                      className="h-20 w-full object-cover"
                    />
                    {coverIndex === i && (
                      <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] bg-supernova-500 text-white py-0.5">
                        Portada
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeExistingImage(i); }}
                      className="absolute top-1 right-1 bg-red-600 text-white p-0.5 rounded"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
                {newImages.map((img, i) => (
                  <div
                    key={i}
                    onClick={() => setCoverIndex(existingImages.length + i)}
                    className={`relative border rounded-lg cursor-pointer overflow-hidden ${
                      coverIndex === existingImages.length + i ? "ring-2 ring-supernova-500" : ""
                    }`}
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt=""
                      className="h-20 w-full object-cover"
                    />
                    {coverIndex === existingImages.length + i && (
                      <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] bg-supernova-500 text-white py-0.5">
                        Portada
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeNewImage(i); }}
                      className="absolute top-1 right-1 bg-red-600 text-white p-0.5 rounded"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Horario ──────────────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Horario de atención</h3>
            <select
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              className="w-full p-3 border rounded-xl mb-3 text-sm"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </option>
              ))}
            </select>

            <div className="flex flex-col gap-1.5">
              {/* Encabezado de columnas — solo tiene sentido en la grilla de 4 columnas
                  (sm+); en móvil cada fila se apila en 2x2 y lleva sus propias
                  etiquetas chiquitas sobre cada campo de hora. */}
              <div className="hidden sm:grid grid-cols-4 gap-2 text-xs text-gray-400 font-semibold px-2">
                <span>Día</span>
                <span>Abre</span>
                <span>Cierra</span>
                <span className="text-center">Cerrado</span>
              </div>
              {form.schedule.map((d, i) => (
                <div
                  key={d.day}
                  className={`grid grid-cols-2 sm:grid-cols-4 items-center gap-x-2 gap-y-1.5 px-3 py-2.5 rounded-lg ${
                    d.closed ? "bg-gray-50 opacity-60" : "bg-white border border-gray-200"
                  }`}
                >
                  <span className="order-1 text-sm font-semibold text-gray-700">{DAY_LABELS_ES[d.day]}</span>
                  <label className="order-2 sm:order-4 flex items-center gap-1.5 cursor-pointer justify-self-end sm:justify-self-center">
                    <input
                      type="checkbox"
                      checked={d.closed}
                      onChange={() => {
                        const s = [...form.schedule];
                        s[i] = { ...s[i], closed: !s[i].closed };
                        setForm((p) => ({ ...p, schedule: s }));
                      }}
                      className="w-4 h-4 accent-red-500"
                    />
                    <span className="text-xs text-gray-500">Cerrado</span>
                  </label>
                  <div className="order-3 sm:order-2">
                    <span className="block text-[10px] text-gray-400 mb-0.5 sm:hidden">Abre</span>
                    <input
                      type="time"
                      value={d.open}
                      disabled={d.closed}
                      onChange={(e) => {
                        const s = [...form.schedule];
                        s[i] = { ...s[i], open: e.target.value };
                        setForm((p) => ({ ...p, schedule: s }));
                      }}
                      className="border rounded px-2 py-1 text-sm w-full disabled:bg-gray-100 border-gray-300"
                    />
                  </div>
                  <div className="order-4 sm:order-3">
                    <span className="block text-[10px] text-gray-400 mb-0.5 sm:hidden">Cierra</span>
                    <input
                      type="time"
                      value={d.close}
                      disabled={d.closed}
                      onChange={(e) => {
                        const s = [...form.schedule];
                        s[i] = { ...s[i], close: e.target.value };
                        setForm((p) => ({ ...p, schedule: s }));
                      }}
                      className="border rounded px-2 py-1 text-sm w-full disabled:bg-gray-100 border-gray-300"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Redes Sociales ───────────────────────────────────── */}
          <div>
            <h3 className="font-semibold mb-2 text-gray-700">
              Redes Sociales{" "}
              <span className="font-normal text-gray-400 text-sm">(opcional)</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input type="text" placeholder="Facebook URL"  name="facebook"  value={form.social.facebook}  onChange={handleChange} className="p-3 border border-gray-300 rounded-xl" />
              <input type="text" placeholder="Instagram URL" name="instagram" value={form.social.instagram} onChange={handleChange} className="p-3 border border-gray-300 rounded-xl" />
              <input type="text" placeholder="Sitio Web"     name="web"       value={form.social.web}       onChange={handleChange} className="p-3 border border-gray-300 rounded-xl sm:col-span-2" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-supernova-500 text-white py-3 rounded-xl font-semibold disabled:opacity-60"
          >
            {loading ? "Guardando..." : sedeToEdit ? "Actualizar sede" : "Crear sede"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
